import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { and } from '@adornis/validation/functions/and.js';
import { email } from '@adornis/validation/functions/email.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { CreateUserOptions, UserAPI } from '@saraa/permissions/api/UserAPI.js';
import { SaraaOrgRole } from '@saraa/permissions/db/SaraaUser.js';
import { html } from 'lit';
import { OrganizationSelect } from './OrganizationSelect.js';
import { dialogHeader } from './ui.js';

const ROLE_LABELS: Record<SaraaOrgRole, string> = {
  [SaraaOrgRole.ORG_ADMIN]: 'Organization admin',
  [SaraaOrgRole.ORG_USER]: 'Organization user',
};

const CreateUserDialog = stateful((props: { resolve: (val: unknown) => void; reject: () => void }) => {
  const [newUser] = useState({ email: '', organizationId: '', role: SaraaOrgRole.ORG_USER });

  const form = useForm(newUser, {
    validator: { email: and(nonOptional(), email()), organizationId: nonOptional(), role: nonOptional() },
  });

  const handleCreateUser = async () => {
    const errors = await form.validate();
    if (errors) return;

    await guardDialog(
      async () =>
        await UserAPI.create(
          new CreateUserOptions({
            email: form.document.email,
            organizationId: form.document.organizationId,
            role: form.document.role,
          }),
        )(),
      {
        success: () => {
          props.resolve(newUser);
          XSnackbar.show('User created successfully');
        },
      },
    );
  };

  return html`
    <div class="flex flex-col gap-4">
      ${dialogHeader({ title: 'Create user', close: props.reject })}

      <div class="flex flex-col gap-1.5">
        <p class="text-base-content text-sm font-medium">Email address</p>
        ${TextInput({ ...form.field('email'), placeholder: 'name@example.org', placeholderMode: 'hide' })}
      </div>

      <div class="flex flex-col gap-1.5">
        <p class="text-base-content text-sm font-medium">Organization</p>
        ${OrganizationSelect({ ...form.field('organizationId') })}
      </div>

      <div class="flex flex-col gap-1.5">
        <p class="text-base-content text-sm font-medium">Role</p>
        ${DropdownSelectionInput<SaraaOrgRole>({
          ...form.field('role'),
          selectables: Object.values(SaraaOrgRole),
          renderString: role => ROLE_LABELS[role] ?? role,
        })}
        <p class="text-base-content/60 text-xs">
          The role applies to this organization only. Further memberships can be added on the user's detail page.
        </p>
      </div>

      <button class="btn btn-primary" @click=${handleCreateUser}>Create user</button>
    </div>
  `;
});

export async function openCreateUserDialog() {
  return showDialog({
    content: (resolve, reject) => CreateUserDialog({ resolve, reject }),
    // Three fields do not need `max-w-4xl`. Sized to the form so the inputs stay a
    // readable length instead of stretching the width of a laptop screen.
    modalBoxClasses: 'max-w-lg',
  });
}
